import React, { useState, useEffect } from 'react'
import styled, { keyframes, css } from 'styled-components'
import moment from 'moment'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import GavelIcon from '@mui/icons-material/Gavel'
import NextOccurrenceBadge from 'features/taskManagement/NextOccurrenceBadge'
import TaskTypeBadge from 'features/taskManagement/components/TaskTypeBadge'
import TaskCadenceBadge from 'features/taskManagement/components/TaskCadenceBadge'
import TaskPeopleAvatars from 'features/taskManagement/components/TaskPeopleAvatars'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined'
import Tooltip from '@mui/material/Tooltip'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import DragHandleIcon from '@mui/icons-material/DragHandle'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import TaskFormQuickEdit from './TaskFormQuickEdit'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'

const priorityConfig = {
  low: { label: PRIORITY_META.low.label, color: PRIORITY_META.low.fg, bg: PRIORITY_META.low.bg, icon: KeyboardArrowDownIcon },
  medium: { label: PRIORITY_META.medium.label, color: PRIORITY_META.medium.fg, bg: PRIORITY_META.medium.bg, icon: DragHandleIcon },
  high: { label: PRIORITY_META.high.label, color: PRIORITY_META.high.fg, bg: PRIORITY_META.high.bg, icon: KeyboardArrowUpIcon },
  critical: { label: PRIORITY_META.critical.label, color: PRIORITY_META.critical.fg, bg: PRIORITY_META.critical.bg, icon: KeyboardDoubleArrowUpIcon }
}

const KANBAN_COLUMNS = ['unassigned', 'assigned', 'in_progress', 'on_hold', 'awaiting_approval', 'completed'].map(key => {
  const meta = taskStatusMeta(key)
  return { key, label: meta.label, color: meta.color, bg: meta.bg, accent: meta.color }
})

const CATEGORY_TO_COLUMN = {
  on_hold: 'on_hold',
  completed: 'completed',
  completed_late: 'completed',
  awaiting_approval: 'awaiting_approval'
}

const buildExpandedState = (focusColumn) =>
  KANBAN_COLUMNS.reduce((acc, col) => {
    acc[col.key] = focusColumn
      ? col.key === focusColumn
      : (col.key !== 'on_hold' && col.key !== 'completed')
    return acc
  }, {})

const getUploadAction = task => {
  const actionType = task?.metadata?.taskActionType || task?.metadata?.task_action_type
  if (actionType === 'upload_multiple') {
    return { label: 'Upload multiple files', Icon: FileCopyOutlinedIcon }
  }
  if (actionType === 'upload_single') {
    return { label: 'Upload file', Icon: UploadFileOutlinedIcon }
  }
  return null
}

const TaskKanban = ({ tasks, onStarTask, searchValue, selectedTaskIds = [], onToggleSelect, selectedCategory }) => {
  const hasSelection = selectedTaskIds.length > 0
  const focusColumn = CATEGORY_TO_COLUMN[selectedCategory] || null
  const [expandedSections, setExpandedSections] = useState(() => buildExpandedState(focusColumn))

  useEffect(() => {
    setExpandedSections(buildExpandedState(CATEGORY_TO_COLUMN[selectedCategory] || null))
  }, [selectedCategory])

  const filteredTasks = tasks

  const getColumnTasks = (statusKey) => {
    return filteredTasks.filter(t => {
      if (statusKey === 'unassigned') return t.status === 'unassigned'
      if (statusKey === 'assigned') return t.status === 'assigned' || t.status === 'pending_dependent'
      return t.status === statusKey
    })
  }

  const formatDeadline = (date) => {
    if (!date) return null
    const deadline = moment(date)
    const today = moment().startOf('day')
    const diffDays = deadline.diff(today, 'days')
    if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays <= 7) return deadline.format('ddd')
    return deadline.format('DD MMM')
  }

  const formatDeadlineShort = (date) => {
    const deadline = moment(date)
    const diffDays = deadline.diff(moment().startOf('day'), 'days')
    if (diffDays < 0) return `${Math.abs(diffDays)}d`
    if (diffDays === 0) return 'Today'
    if (diffDays <= 7) return `${diffDays}d`
    return deadline.format('D MMM')
  }

  const deadlineTooltip = (date, overdue) => {
    const deadline = moment(date)
    const abs = Math.abs(deadline.diff(moment().startOf('day'), 'days'))
    const dateStr = deadline.format('ddd D MMM YYYY')
    if (overdue) return `Due ${dateStr}: ${abs} ${abs === 1 ? 'day' : 'days'} overdue`
    return `Due ${dateStr}`
  }

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const renderPeople = (task) => {
    return (
      <PeopleRow onClick={(e) => e.stopPropagation()}>
        <TaskPeopleAvatars task={task} max={3} emptyLabel="+ People" />
      </PeopleRow>
    )
  }

  // Desktop card — full detail
  const renderCard = (task, column) => {
    const isOverdue = task.isOverdue && task.status !== 'completed'
    const isSelected = selectedTaskIds.includes(task.id)
    const unitList = task.units?.length > 0 ? task.units : (task.unit ? [task.unit] : [])
    const hasMeta = task.building || unitList.length > 0 || task.formTemplate || task.team || task.project
    const showProgress = task.progress > 0 && task.status !== 'completed'
    const uploadAction = getUploadAction(task)
    const UploadActionIcon = uploadAction?.Icon
    return (
      <KanbanCard
        key={task.id}
        $isOverdue={isOverdue}
        $selected={isSelected}
        $domainColour={task.taskDomain?.colour}
        onClick={() => redirect(PRIVATE_PATHS.taskDetails.replace(':id', task.id))}
      >
        {task.taskDomain
          ? (
            <Tooltip title={task.taskDomain.name} arrow placement="top">
              <DomainRail $colour={task.taskDomain.colour}>
                <DomainRailText $colour={task.taskDomain.colour}>{task.taskDomain.short || task.taskDomain.name}</DomainRailText>
              </DomainRail>
            </Tooltip>
          )
          : (isOverdue ? <OverdueRail /> : null)}
        <CardInner>
          <CardTop>
            <CardCheckbox
              type="checkbox"
              checked={isSelected}
              $visible={hasSelection || isSelected}
              onChange={() => onToggleSelect?.(task.id)}
              onClick={(e) => e.stopPropagation()}
            />
            <Tooltip title={task.title} arrow placement="top">
              <CardTitle>{task.title}</CardTitle>
            </Tooltip>
            <StarBtn
              $starred={task.isStarred}
              onClick={(e) => {
                e.stopPropagation()
                onStarTask(task.id)
              }}
            >
              {task.isStarred ? <StarIcon /> : <StarBorderIcon />}
            </StarBtn>
          </CardTop>

          <CardTypeRow><TaskTypeBadge task={task} /><TaskCadenceBadge task={task} /></CardTypeRow>

          {hasMeta && (
            <MetaRow>
              {task.building && (
                <Tooltip title={task.building.name} arrow placement="top">
                  <MetaChip>
                    <ApartmentOutlinedIcon />
                    <span>{task.building.name}</span>
                  </MetaChip>
                </Tooltip>
              )}
              {unitList.length > 0 && (
                <Tooltip title={unitList.map(u => u.name).join(', ')} arrow placement="top">
                  <MetaChip>
                    <MeetingRoomOutlinedIcon />
                    <span>{unitList[0].name}{unitList.length > 1 ? ` +${unitList.length - 1}` : ''}</span>
                  </MetaChip>
                </Tooltip>
              )}
              {task.formTemplate && (
                <TaskFormQuickEdit task={task}>
                  <Tooltip
                    title={uploadAction?.label || `Form: ${task.formTemplate.name}`}
                    arrow
                    placement="top"
                  >
                    <MetaChip $accent="#5b21b6" $bg="#ede9fe">
                      {uploadAction
                        ? <UploadActionIcon />
                        : <DescriptionOutlinedIcon />}
                    </MetaChip>
                  </Tooltip>
                </TaskFormQuickEdit>
              )}
              {task.team && (
                <Tooltip title={`Team: ${task.team.name}`} arrow placement="top">
                  <MetaChip $accent="#0d7377" $bg="#e0f7f7">
                    <GroupOutlinedIcon />
                  </MetaChip>
                </Tooltip>
              )}
              {task.project && (
                <Tooltip title={`Project: ${task.project.name}`} arrow placement="top">
                  <MetaChip $accent="#0e7490" $bg="#e0f2fe">
                    <FolderOutlinedIcon />
                  </MetaChip>
                </Tooltip>
              )}
            </MetaRow>
          )}

          {task.status === 'awaiting_approval' && task.permissions?.canApprove && (
            <CardApproveBtn
              onClick={(e) => { e.stopPropagation(); redirect(PRIVATE_PATHS.taskDetails.replace(':id', task.id)) }}
              title="Review & Approve"
            >
              ✓ Review &amp; Approve
            </CardApproveBtn>
          )}

          <CardBottom>
            <CardBottomLeft>
              {task.priority && priorityConfig[task.priority] && (() => {
                const PIcon = priorityConfig[task.priority].icon
                return (
                  <Tooltip title={`${priorityConfig[task.priority].label} priority`} arrow placement="top">
                    <PriorityBadge $color={priorityConfig[task.priority].color}>
                      <PIcon />
                      {priorityConfig[task.priority].label}
                    </PriorityBadge>
                  </Tooltip>
                )
              })()}
              {task.deadline && (
                <Tooltip title={deadlineTooltip(task.deadline, isOverdue)} arrow placement="top">
                  <DeadlineBadge $isOverdue={isOverdue}>
                    <CalendarTodayIcon />
                    {formatDeadlineShort(task.deadline)}
                  </DeadlineBadge>
                </Tooltip>
              )}
              <NextOccurrenceBadge task={task} compact />
              {task.createdAt && (
                <Tooltip title={`Created ${moment(task.createdAt).format('DD MMM YYYY')}`} arrow placement="top">
                  <CreatedBadge>
                    <AccessTimeIcon />
                  </CreatedBadge>
                </Tooltip>
              )}
            </CardBottomLeft>

          </CardBottom>

          {renderPeople(task)}

          {task.status === 'pending_dependent' && (
            <PendingBadge>Blocked by dependency</PendingBadge>
          )}
        </CardInner>
        {showProgress && (
          <CardProgressStrip $progress={task.progress} $color={column.accent} title={`${task.progress}% complete`} />
        )}
      </KanbanCard>
    )
  }

  // Mobile card — compact horizontal layout
  const renderMobileCard = (task, column) => {
    const isOverdue = task.isOverdue && task.status !== 'completed'
    return (
      <MobileCard
        key={task.id}
        $isOverdue={isOverdue}
        $domainColour={task.taskDomain?.colour}
        onClick={() => redirect(PRIVATE_PATHS.taskDetails.replace(':id', task.id))}
      >
        {task.progress > 0 && task.status !== 'completed' && (
          <MobileProgressStrip $progress={task.progress} $color={column.accent} />
        )}
        <MobileCardBody>
          <MobileCardMain>
            <MobileCardTitle>{task.title}</MobileCardTitle>
            <CardTypeRow><TaskTypeBadge task={task} /><TaskCadenceBadge task={task} /></CardTypeRow>
            {task.taskDomain && (
              <DomainRow>
                <DomainChip $colour={task.taskDomain.colour}>
                  <DomainSwatch $colour={task.taskDomain.colour} />
                  <span>{task.taskDomain.short || task.taskDomain.name}</span>
                </DomainChip>
              </DomainRow>
            )}
            <MobileCardMeta>
              {task.building && (
                <MobileMetaItem>
                  <ApartmentOutlinedIcon />
                  <span>{task.building.name}</span>
                </MobileMetaItem>
              )}
              {(() => {
                const unitList = task.units?.length > 0 ? task.units : (task.unit ? [task.unit] : [])
                if (unitList.length === 0) return null
                return (
                  <MobileMetaItem>
                    <MeetingRoomOutlinedIcon />
                    <span>
                      {unitList[0].name}
                      {unitList.length > 1 && ` +${unitList.length - 1}`}
                    </span>
                  </MobileMetaItem>
                )
              })()}
              {task.team && (
                <MobileMetaItem>
                  <GroupOutlinedIcon />
                  <span>{task.team.name}</span>
                </MobileMetaItem>
              )}
              {task.priority && priorityConfig[task.priority] && (() => {
                const PIcon = priorityConfig[task.priority].icon
                return (
                  <MobilePriority $color={priorityConfig[task.priority].color}>
                    <PIcon />
                    {priorityConfig[task.priority].label}
                  </MobilePriority>
                )
              })()}
              {task.deadline && (
                <MobileDeadline $isOverdue={isOverdue}>
                  <CalendarTodayIcon />
                  {formatDeadline(task.deadline)}
                </MobileDeadline>
              )}
              {task.assignees?.length > 0 && (
                <MobileAssignees>
                  <PersonOutlineIcon />
                  <span>{task.assignees.length}</span>
                </MobileAssignees>
              )}
              {task.status === 'pending_dependent' && (
                <MobileBlocked>Blocked</MobileBlocked>
              )}
            </MobileCardMeta>
          </MobileCardMain>
          <MobileStarBtn
            $starred={task.isStarred}
            onClick={(e) => {
              e.stopPropagation()
              onStarTask(task.id)
            }}
          >
            {task.isStarred ? <StarIcon /> : <StarBorderIcon />}
          </MobileStarBtn>
        </MobileCardBody>
      </MobileCard>
    )
  }

  return (
    <KanbanWrapper>
      {/* Desktop: horizontal columns */}
      <DesktopBoard>
        {KANBAN_COLUMNS.map(column => {
          const columnTasks = getColumnTasks(column.key)
          const isExpanded = expandedSections[column.key]

          if (!isExpanded) {
            return (
              <CollapsedColumn
                key={column.key}
                onClick={() => toggleSection(column.key)}
                title={`Show ${column.label}`}
              >
                <CollapsedChevron><ChevronRightIcon /></CollapsedChevron>
                <ColumnDot $color={column.accent} />
                <CollapsedCount $color={column.color} $bg={column.bg}>{columnTasks.length}</CollapsedCount>
                <CollapsedTitle>{column.label}</CollapsedTitle>
              </CollapsedColumn>
            )
          }

          return (
            <Column key={column.key}>
              <ColumnHeader onClick={() => toggleSection(column.key)} title={`Collapse ${column.label}`}>
                <ColumnDot $color={column.accent} />
                <ColumnTitle>{column.label}</ColumnTitle>
                <ColumnCount $color={column.color} $bg={column.bg}>{columnTasks.length}</ColumnCount>
                <CollapseBtn data-collapse aria-label={`Collapse ${column.label}`}><ChevronLeftIcon /></CollapseBtn>
              </ColumnHeader>

              <CardList>
                {columnTasks.map(task => renderCard(task, column))}
                {columnTasks.length === 0 && (
                  <EmptyColumn>No tasks</EmptyColumn>
                )}
              </CardList>
            </Column>
          )
        })}
      </DesktopBoard>

      {/* Mobile: collapsible accordion sections */}
      <MobileBoard>
        {KANBAN_COLUMNS.map(column => {
          const columnTasks = getColumnTasks(column.key)
          const isExpanded = expandedSections[column.key]
          const isEmpty = columnTasks.length === 0
          return (
            <MobileSection key={column.key} $isEmpty={isEmpty}>
              <MobileSectionHeader
                onClick={() => toggleSection(column.key)}
                $accentColor={column.accent}
                $isEmpty={isEmpty}
              >
                <MobileSectionAccent $color={column.accent} />
                <MobileSectionTitle $isEmpty={isEmpty}>{column.label}</MobileSectionTitle>
                <ColumnCount $color={column.color} $bg={column.bg}>{columnTasks.length}</ColumnCount>
                <MobileChevron $expanded={isExpanded}>
                  <ExpandMoreIcon />
                </MobileChevron>
              </MobileSectionHeader>

              {isExpanded && (
                <MobileSectionContent>
                  {columnTasks.map(task => renderMobileCard(task, column))}
                  {isEmpty && (
                    <MobileEmptyText>No tasks in this column</MobileEmptyText>
                  )}
                </MobileSectionContent>
              )}
            </MobileSection>
          )
        })}
      </MobileBoard>
    </KanbanWrapper>
  )
}

export default TaskKanban

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`

const KanbanWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

// ─── Desktop board ───

const DesktopBoard = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px 12px;
  align-items: flex-start;

  @media (max-width: 768px) {
    display: none;
  }
`

const Column = styled.div`
  flex: 1 1 0;
  min-width: 0;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
`

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  flex-shrink: 0;
  position: sticky;
  top: 150px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px 12px 0 0;
  z-index: 2;
  cursor: pointer;

  &:hover [data-collapse] {
    opacity: 1;
  }
`

const CollapseBtn = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#9ca3af'};
  opacity: 0.45;
  transition: opacity 0.15s;

  svg { font-size: 18px; }
`

const CollapsedColumn = styled.div`
  flex: 0 0 46px;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 0;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }
`

const CollapsedChevron = styled.span`
  display: flex;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#9ca3af'};

  svg { font-size: 18px; }
`

const CollapsedCount = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 2px 7px;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
`

const CollapsedTitle = styled.span`
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  margin-top: 2px;
`

const ColumnDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

const ColumnTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  flex: 1;
`

const ColumnCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 2px 8px;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
`

const CardList = styled.div`
  padding: 0 8px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

// ─── Desktop card ───

const KanbanCard = styled.div`
  display: flex;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid ${({ $selected }) => ($selected ? '#33becc' : 'rgba(0, 0, 0, 0.04)')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.15s;
  animation: ${fadeIn} 0.2s ease-out;
  ${({ $selected }) => $selected && 'background: #e6f7f9;'}

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  &:hover input[type="checkbox"] {
    opacity: 1;
    width: 15px;
    margin-right: 0;
  }
`

const DomainRail = styled.div`
  flex-shrink: 0;
  align-self: stretch;
  width: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
  background: ${({ $colour }) => `${$colour}12`};
  border-right: 2px solid ${({ $colour }) => $colour};
`

const DomainRailText = styled.span`
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.4px;
  color: ${({ $colour }) => $colour};
  white-space: nowrap;
  pointer-events: none;
`

const OverdueRail = styled.div`
  flex-shrink: 0;
  width: 3px;
  align-self: stretch;
  background: #dc2626;
`

const CardInner = styled.div`
  flex: 1;
  min-width: 0;
  padding: 14px;
`

const CardCheckbox = styled.input`
  width: ${({ $visible }) => ($visible ? '15px' : '0')};
  height: 15px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: #0C1229;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  margin-right: ${({ $visible }) => ($visible ? '0' : '-8px')};
  transition: opacity 0.15s;
`

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
`

const CardTypeRow = styled.div`
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.35;
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const StarBtn = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  color: ${({ $starred }) => $starred ? '#faad14' : '#d1d5db'};

  svg { font-size: 18px; }

  &:hover { color: #faad14; }
`

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  margin: 8px 0 2px;
`

const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-width: 0;
  padding: 3px 7px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ $accent }) => $accent || '#475569'};
  background: ${({ $bg, theme }) => $bg || theme.colors.lightGrey || '#f1f5f9'};
  box-sizing: border-box;
  cursor: default;

  svg { font-size: 13px; flex-shrink: 0; opacity: 0.9; }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    line-height: 1.2;
  }
`

const CardProgressStrip = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: ${({ theme }) => theme.colors.mediumGrey};

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $progress }) => $progress}%;
    background: ${({ $color }) => $color || '#059669'};
  }
`

const CardApproveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 8px 12px;
  margin-top: 8px;
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 18px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.haiti};
    box-shadow: 0 3px 10px rgba(41, 152, 163, 0.25);
  }
`

const CardBottom = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  margin-top: 4px;
`

const CardBottomLeft = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  row-gap: 4px;
  min-width: 0;
`

const DomainRow = styled.div`
  display: flex;
  min-width: 0;
  margin-bottom: 8px;
`

const DomainChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: ${({ $colour }) => $colour || '#475569'};
  background-color: ${({ $colour }) => `${$colour || '#64748b'}14`};
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1;
    min-width: 0;
  }
`

const DomainSwatch = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ $colour }) => $colour || '#64748b'};
`

const DeadlineBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $isOverdue }) => $isOverdue ? '#dc2626' : '#6b7280'};
  ${({ $isOverdue }) => $isOverdue && 'background: #fee2e2; padding: 1px 6px; border-radius: 5px;'}

  svg { font-size: 12px; }
`

const CreatedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  color: #9ca3af;
  cursor: help;

  svg { font-size: 13px; }
`

const PriorityBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $color }) => $color};

  svg { font-size: 14px; }
`

const ApprovalIcon = styled.div`
  color: #7c3aed;
  display: flex;
  svg { font-size: 14px; }
`

const PeopleRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
`

const PendingBadge = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #d97706;
  background: #fef3c7;
  padding: 4px 8px;
  border-radius: 4px;
  margin-top: 8px;
  text-align: center;
`

const EmptyColumn = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  font-style: italic;
`

// ─── Mobile board ───

const MobileBoard = styled.div`
  display: none;
  flex-direction: column;
  gap: 6px;
  padding: 12px;

  @media (max-width: 768px) {
    display: flex;
  }
`

const MobileSection = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  ${({ $isEmpty }) => $isEmpty && 'opacity: 0.7;'}
`

const MobileSectionAccent = styled.div`
  width: 3px;
  align-self: stretch;
  background: ${({ $color }) => $color};
  border-radius: 3px;
  flex-shrink: 0;
`

const MobileSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 14px 14px 0;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  min-height: 48px;

  &:active {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const MobileChevron = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  transition: transform 0.25s ease;
  transform: rotate(${({ $expanded }) => $expanded ? '180deg' : '0deg'});
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};

  svg { font-size: 22px; }
`

const MobileSectionTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $isEmpty, theme }) => $isEmpty ? (theme.colors.grey?.[800] || '#999') : theme.colors.haiti};
  flex: 1;
`

const sectionFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`

const MobileSectionContent = styled.div`
  padding: 0 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  animation: ${sectionFadeIn} 0.2s ease-out;
`

const MobileEmptyText = styled.div`
  padding: 16px 8px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  font-style: italic;
`

// ─── Mobile card — compact layout ───

const MobileCard = styled.div`
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  flex-shrink: 0;
  overflow: hidden;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  position: relative;

  ${({ $isOverdue, $domainColour }) => $domainColour
    ? css`
      border-left: 3px solid ${$domainColour};
    `
    : ($isOverdue && css`
      border-left: 3px solid #dc2626;
      background: #fff8f7;
    `)}

  &:active {
    opacity: 0.85;
    transform: scale(0.98);
  }
`

const MobileProgressStrip = styled.div`
  height: 3px;
  background: ${({ theme }) => theme.colors.mediumGrey};

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $progress }) => $progress}%;
    background: ${({ $color }) => $color || '#059669'};
    border-radius: 0 2px 2px 0;
  }
`

const MobileCardBody = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
`

const MobileCardMain = styled.div`
  flex: 1;
  min-width: 0;
`

const MobileCardTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const MobileCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
  flex-wrap: wrap;
`

const MobileMetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
  max-width: 160px;
  min-width: 0;
  box-sizing: border-box;
  padding: 2px 6px;
  background: ${({ theme }) => theme.colors.lightGrey || '#f5f5f5'};
  border-radius: 4px;

  svg { font-size: 13px; opacity: 0.7; flex-shrink: 0; }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1;
    min-width: 0;
  }
`

const MobilePriority = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $color }) => $color};

  svg { font-size: 13px; }
`

const MobileDeadline = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $isOverdue }) => $isOverdue ? '#dc2626' : '#6b7280'};
  ${({ $isOverdue }) => $isOverdue && 'background: #fee2e2; padding: 1px 5px; border-radius: 3px;'}

  svg { font-size: 11px; }
`

const MobileAssignees = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};

  svg { font-size: 14px; opacity: 0.6; }
`

const MobileApproval = styled.div`
  display: flex;
  align-items: center;
  color: #7c3aed;

  svg { font-size: 13px; }
`

const MobileBlocked = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #d97706;
  background: #fef3c7;
  padding: 1px 6px;
  border-radius: 3px;
`

const MobileStarBtn = styled.button`
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  color: ${({ $starred }) => $starred ? '#faad14' : '#d1d5db'};
  -webkit-tap-highlight-color: transparent;
  border-radius: 50%;

  svg { font-size: 20px; }

  &:active {
    background: rgba(0, 0, 0, 0.05);
    color: #faad14;
  }
`
